import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';

import {
  bootstrapServerSession,
  clearLegacyTokens,
  followSessionInOtherTabs,
} from './api/sessionAPI';
import App from './app';
import SessionBootSplash from './components/session/SessionBootSplash';
import { captureViewAsHandoff, isViewAs } from './viewAs/viewAsSession';
import ViewAsRoot from './viewAs/ViewAsRoot';

// HA-447: a support view-as tab takes its session token out of the URL before Sentry or the router
// can record it, and never runs Session Replay: the screens show a practice's client data.
captureViewAsHandoff();
const viewAs = isViewAs();

Sentry.init({
  dsn: 'https://28e00d97fc3791e63a87d5ca7c7c283b@o4508237537869824.ingest.us.sentry.io/4508348207333376',
  // HA-410: tags every event with which of the 4 build environments produced
  // it (development/iis/production - see .env.local/.env.dev/.env.iis/.env.production),
  // so real production signal can be filtered from dev/internal-testing noise
  // in the Sentry UI without dropping any environment's error visibility.
  environment: process.env.SENTRY_ENVIRONMENT || 'unknown',
  integrations: viewAs
    ? [Sentry.browserTracingIntegration()]
    : [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // HA-410: the previous entry here was an unmodified placeholder host
  // straight from Sentry's own setup-wizard docs, and never matched any
  // real request this app makes - distributed tracing to the backend has
  // never actually worked. These patterns match this app's real API hosts
  // across all 4 environments.
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/local\.healiospace\.com\/api/,
    /^https:\/\/api\.healiospace\.com\//,
    /^https:\/\/.*\.execute-api\.[a-z0-9-]+\.amazonaws\.com/,
  ],
  // Session Replay
  replaysSessionSampleRate: viewAs ? 0 : 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: viewAs ? 0 : 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

document.body.innerHTML = '<div id="app"></div>';

const container = document.getElementById('app') ?? document.body;
const root = createRoot(container);

// HA-445: who the user is comes from the API's session, read before the app renders (the router
// decides on it synchronously); a loading screen shows meanwhile. A view-as tab has its own session
// (HA-447) and leaves the admin's cookies alone.
if (!viewAs) {
  root.render(<SessionBootSplash />);
}

const sessionReady = viewAs
  ? Promise.resolve()
  : bootstrapServerSession().then(() => {
      clearLegacyTokens();
      followSessionInOtherTabs();
    });

void sessionReady.then(() =>
  root.render(
    <React.StrictMode>
      {viewAs ? (
        <ViewAsRoot>
          <App />
        </ViewAsRoot>
      ) : (
        <App />
      )}
    </React.StrictMode>
  )
);
